/**
 * Empty state for the vendors list — first-run + filter-no-match.
 *
 * Mirrors `features/customers/components/empty-state.tsx`. Could
 * generalise later if we get a third master vertical and the copy
 * starts feeling templated.
 */
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { Card, CardBody } from "@flint/ui";

export interface EmptyStateProps {
  variant: "first-run" | "no-archived" | "no-search-match";
  query?: string;
}

export function EmptyState({ variant, query }: EmptyStateProps) {
  if (variant === "no-search-match") {
    return (
      <Card>
        <CardBody className="text-center py-10">
          <p className="font-medium">No vendors match {query ? <>“{query}”</> : "this search"}.</p>
          <p className="text-sm text-muted mt-1">Try a name prefix, or paste the full GSTIN.</p>
        </CardBody>
      </Card>
    );
  }

  if (variant === "no-archived") {
    return (
      <Card>
        <CardBody className="text-center py-10">
          <p className="font-medium">No archived vendors.</p>
          <p className="text-sm text-muted mt-1">Archived rows show up here once you retire one.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className="text-center py-10">
        <p className="text-base font-medium">No vendors yet.</p>
        <p className="text-sm text-muted mt-1 max-w-md mx-auto">
          Add your first supplier to start recording bills. You can paste a GSTIN to fill the legal
          details, or skip and add them later.
        </p>
        <Link
          to="/procurement/vendors/new"
          className="inline-flex items-center justify-center gap-1.5 mt-4 h-9 px-4 rounded-sm bg-ink text-paper text-sm font-medium hover:bg-ink-2"
        >
          <Plus size={13} strokeWidth={2.4} aria-hidden />
          Add your first vendor
        </Link>
      </CardBody>
    </Card>
  );
}
